import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { patientIntakeApi } from "@/lib/patientIntakeApi";

type CallbackStatus = "loading" | "success" | "error";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const formUid = searchParams.get("formUid");
        const referenceUid = searchParams.get("referenceUid");

        if (!accessToken) {
          setStatus("error");
          setErrorMessage("We couldn't find a valid access token in the callback URL.");
          return;
        }

        if (!formUid || !referenceUid) {
          setStatus("error");
          setErrorMessage("Missing intake reference details. Please restart your intake verification.");
          return;
        }

        const result = await patientIntakeApi.completeMagicLinkCallback({
          accessToken,
          refreshToken: refreshToken || undefined,
          formUid,
          referenceUid,
        });

        if (result.data?.accessToken) {
          localStorage.setItem("accessToken", result.data.accessToken);
          if (result.data.refreshToken) {
            localStorage.setItem("refreshToken", result.data.refreshToken);
          }
        }

        setStatus("success");

        const redirectTarget =
          result.data?.redirectUrl ||
          import.meta.env.VITE_JEMA_MANAGER_URL ||
          "http://localhost:5173";

        setTimeout(() => {
          window.location.href = redirectTarget;
        }, 2000);
      } catch (error) {
        console.error("Magic link callback error:", error);
        setStatus("error");
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "We couldn't complete your verification. Please try requesting a new link.",
        );
      }
    };

    void handleCallback();
  }, [navigate, searchParams]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#166B93]/10 to-[#CEF12F]/10 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-[#166B93]/10">
              <Loader2 className="h-8 w-8 animate-spin text-[#166B93]" />
            </div>
            <CardTitle className="text-xl font-semibold text-[#0F4461]">
              Finalizing your verification
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center text-sm text-gray-600 space-y-2">
            <p>Please wait while we verify your email and secure your intake.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#166B93]/10 to-[#CEF12F]/10 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl font-semibold text-emerald-700">
              Email verified successfully!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center text-sm text-gray-600 space-y-3">
            <p>You're all set. We're redirecting you to the next step.</p>
            <div className="text-xs text-gray-500">You can close this window if nothing happens.</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-white p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-red-50">
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
          <CardTitle className="text-xl font-semibold text-red-600">
            Verification link expired
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4 text-sm text-gray-600">
          <p>{errorMessage || "We couldn't verify this link. It may have expired or already been used."}</p>
          <div className="space-y-2">
            <Button
              className="w-full bg-[#166B93] hover:bg-[#125a7a]"
              onClick={() => navigate("/")}
            >
              Start a New Intake
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.replace("/")}
            >
              Return to Homepage
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Need help? Contact support so we can send you another verification link.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
